import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";

/**
 * One slice through the orientation sweep: yield against tilt, or against azimuth.
 *
 * SIZE
 * ----
 * It takes half the width of the panel beside the angle fields, and is drawn
 * in real pixels at whatever width that is: the viewBox is the measured width,
 * so the text stays at a fixed 11-12px. A fixed viewBox scaled up with the
 * panel, and at full width the tick labels came out larger than the form's
 * own text.
 *
 * TWO MARKS, AND THEY SAY DIFFERENT THINGS
 * ----------------------------------------
 * The optimum is where the curve peaks. The project's current orientation is
 * wherever it happens to sit, and the vertical distance between them IS the
 * "loss with respect to optimum" figure printed beside the chart. Showing both
 * is the point: a recommended angle on its own does not tell anyone whether
 * their existing one is costing them anything.
 *
 * The "now" label carries its own value. It used to be the bare word, printed
 * on the axis where a tick label already was, so a project at 28 degrees read
 * as "n30w" -- the marker and the tick overlapping into nonsense. A tick close
 * enough to collide is now dropped, because the "now" label is the more
 * specific of the two.
 *
 * WHY THE AXIS IS NOT NORMALISED
 * ------------------------------
 * PVsyst draws these normalised to 1.0 at the optimum, which hides what a
 * percent is worth. The y-axis here is kWh/kWp, so a reader can see that the
 * curve is nearly flat across ten degrees of tilt -- which is the actual
 * engineering conclusion most of the time, and it disappears under
 * normalisation.
 */

const DEFAULT_W = 320;   // until the first measurement, and in tests
const VIEW_H = 170;
const PAD = { top: 22, right: 14, bottom: 24, left: 42 };
const FONT = 11;

/** The curve's value at x, read between the two points either side of it. */
function yAt(points, xv) {
  if (xv == null || !Number.isFinite(Number(xv))) return null;
  const t = Number(xv);
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (t >= a.x && t <= b.x && a.y != null && b.y != null) {
      return b.x === a.x ? a.y : a.y + ((t - a.x) / (b.x - a.x)) * (b.y - a.y);
    }
  }
  return null;
}

export default function OrientationProfileChart({
  title,
  points,          // [{ x, y }] ascending in x
  unit = "°",
  optimum,         // { x, y }
  optimumX = null, // or just the x, placed on the curve here
  current,         // { x, y } or null
  caption = null,
  yAxisLabel = null,
  locale = "en-GB",
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const box = useRef(null);
  const [VIEW_W, setWidth] = useState(DEFAULT_W);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!points || points.length < 2) return null;
  // The caller usually knows WHERE the optimum is, not how high the curve is
  // there. This prop was passed as globalOptimumX for a long time and never
  // read, so the "best" mark silently never appeared.
  const bestY = optimum ? null : yAt(points, optimumX);
  const best = optimum || (bestY != null ? { x: Number(optimumX), y: bestY } : null);

  const curve = dark ? "#e8a33c" : "#c2751a";
  const mark = theme.palette.text.primary;
  const grid = theme.palette.divider;
  const muted = theme.palette.text.secondary;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  // The y-window is the data's own range with a little air, not zero-based: the
  // whole question here is how much the curve varies, and anchoring at zero
  // flattens a 6% spread into a straight line.
  const yLo = Math.min(...ys);
  const yHi = Math.max(...ys);
  const pad = Math.max((yHi - yLo) * 0.15, 1);
  const leftPad = Math.max(PAD.left, Math.round(yHi).toLocaleString("en-GB").length * FONT * 0.6 + 12);

  const x = (v) => leftPad + ((v - xMin) / (xMax - xMin || 1)) * (VIEW_W - leftPad - PAD.right);
  const y = (v) =>
    VIEW_H - PAD.bottom - ((v - (yLo - pad)) / ((yHi + pad) - (yLo - pad) || 1)) *
      (VIEW_H - PAD.top - PAD.bottom);

  const path = points
    .map((p, i) => `${i ? "L" : "M"}${x(p.x).toFixed(1)},${y(p.y).toFixed(1)}`)
    .join("");

  // A tick within this much of the "now" mark would print on top of it.
  const COLLISION_PX = 44;
  const ticks = [xMin, (xMin + xMax) / 2, xMax].filter(
    (t) => !current || Math.abs(x(t) - x(current.x)) > COLLISION_PX
  );

  const round = (v) => Math.round(v * 10) / 10;

  return (
    <Box ref={box} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, display: "block", color: "text.secondary", fontSize: 12 }}
      >
        {title}
      </Typography>
      {yAxisLabel && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: 11 }}>
          {yAxisLabel}
        </Typography>
      )}
      <Box
        component="svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        sx={{ width: "100%", height: VIEW_H, display: "block", overflow: "visible", mt: 0.5 }}
      >
        <line x1={leftPad} x2={VIEW_W - PAD.right} y1={y(yLo)} y2={y(yLo)}
              stroke={grid} strokeWidth="1" />
        <line x1={leftPad} x2={VIEW_W - PAD.right} y1={y(yHi)} y2={y(yHi)}
              stroke={grid} strokeWidth="1" />
        <text x={leftPad - 6} y={y(yHi) + 4} textAnchor="end"
              fill={muted} fontSize={FONT}>{Math.round(yHi).toLocaleString("en-GB")}</text>
        <text x={leftPad - 6} y={y(yLo) + 4} textAnchor="end"
              fill={muted} fontSize={FONT}>{Math.round(yLo).toLocaleString("en-GB")}</text>

        {ticks.map((t) => (
          <text key={t} x={x(t)} y={VIEW_H - PAD.bottom + 16} textAnchor="middle"
                fill={muted} fontSize={FONT}>
            {Math.round(t)}{unit}
          </text>
        ))}

        <path d={path} fill="none" stroke={curve} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />

        {best && (
          <>
            <circle cx={x(best.x)} cy={y(best.y)} r="4" fill={curve} />
            <text x={x(best.x)} y={y(best.y) - 8} textAnchor="middle"
                  fill={curve} fontSize={FONT} fontWeight="700">
              best {round(best.x)}{unit}
            </text>
          </>
        )}

        {current && (
          <>
            <line x1={x(current.x)} x2={x(current.x)} y1={y(current.y)} y2={y(yLo)}
                  stroke={mark} strokeOpacity="0.35" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={x(current.x)} cy={y(current.y)} r="4" fill="none"
                    stroke={mark} strokeWidth="1.6" />
            <text x={x(current.x)} y={VIEW_H - PAD.bottom + 16} textAnchor="middle"
                  fill={mark} fontSize={FONT} fontWeight="700">
              now {round(current.x)}{unit}
            </text>
          </>
        )}
      </Box>
      {caption && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: 11, lineHeight: 1.3 }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
}
